import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, User } from "lucide-react";
import { scrollToId } from "../../lib/api";
import ElevareMark from "./ElevareMark";

const links = [
  { label: "Services", id: "services" },
  { label: "How Elevare Works", id: "process" },
  { label: "Initial Estimate", id: "predictor" },
  { label: "Elevare Blueprint", id: "pricing" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#eae7de]/85 border-b border-black/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={() => scrollToId("hero")}
          className="flex items-center gap-3 group"
        >
          <ElevareMark size={48} className="transition-transform duration-300 group-hover:scale-105" />
          <span className="font-head text-lg tracking-tight text-[#1e3d3a]">Elevare</span>
          <span className="hidden sm:inline text-xs uppercase tracking-[0.28em] text-[#c9a961] font-medium">Solutions</span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollToId(l.id)}
              className="text-sm text-black/60 hover:text-[#1e3d3a] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/portal"
            data-testid="nav-portal"
            className="flex items-center gap-1.5 text-sm text-[#1e3d3a]/70 hover:text-[#c9a961] transition-colors duration-300"
            title="Client portal"
          >
            <User className="h-3.5 w-3.5" /> Client login
          </Link>
          <Link
            to="/admin"
            data-testid="nav-admin"
            className="flex items-center gap-1.5 text-sm text-[#1e3d3a]/70 hover:text-[#c9a961] transition-colors duration-300"
            title="Owner dashboard"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Admin
          </Link>
        </nav>

        <button
          data-testid="header-cta"
          onClick={() => scrollToId("contact")}
          className="group flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white pl-5 pr-4 py-2.5 text-sm font-medium hover:bg-[#c9a961] hover:text-black transition-colors duration-300"
        >
          Start Your Business Assessment
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>
    </motion.header>
  );
}
